import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { toast } from 'sonner';
import axios from 'axios';
import { Phone, Upload, X, Compass } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookPilot = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const loadIdParam = searchParams.get('loadId') || '';

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [permitDocs, setPermitDocs] = useState([]);
  const [availableCount, setAvailableCount] = useState(null);

  // Fetch live count of pilots currently listed
  React.useEffect(() => {
    axios.get(`${API}/pilots/availability/public`)
      .then(res => setAvailableCount(Array.isArray(res.data) ? res.data.length : 0))
      .catch(() => setAvailableCount(0));
  }, []);
  const [formData, setFormData] = useState({
    name: '',
    company_name: '',
    email: '',
    phone: '',
    service_required: '',
    accredited_pilots: '',
    number_of_pilots: '',
    police_required: '',
    travel_date: '',
    travel_time: '',
    load_id: loadIdParam,
    pickup_address: '',
    pickup_contact_name: '',
    pickup_contact_phone: '',
    destination_address: '',
    destination_contact_name: '',
    destination_contact_phone: '',
    load_type: '',
    load_height: '',
    load_width: '',
    load_length: '',
    permit_number: '',
    high_load_permit_number: '',
    artc_rail_permit: '',
    telstra_optus_permit: '',
    route_survey: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 10MB)`);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        setPermitDocs(prev => [...prev, { name: file.name, data: reader.result }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const removeDoc = (index) => {
    setPermitDocs(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const required = ['name', 'email', 'phone', 'service_required', 'accredited_pilots', 'number_of_pilots', 'police_required', 'travel_date', 'travel_time', 'pickup_address', 'pickup_contact_name', 'pickup_contact_phone', 'destination_address', 'destination_contact_name', 'destination_contact_phone', 'load_type', 'load_height', 'load_width', 'load_length', 'route_survey'];
    const missing = required.filter(f => !formData[f]);
    if (missing.length) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/pilot-bookings`, {
        ...formData,
        permit_documents: permitDocs.map(d => d.data)
      });
      setSubmitted(true);
      toast.success('Booking request submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit booking');
    } finally {
      setLoading(false);
    }
  };

  const needsCallPrompt = (field) => formData[field] === 'not_sure';

  if (submitted) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
          <Card className="max-w-md w-full mx-4 p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">✓</span>
            </div>
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-2">Booking Submitted</h2>
            <p className="text-gray-600 mb-6">Your pilot/steerer booking request has been received. Our team will be in touch shortly to confirm the details.</p>
            <Button onClick={() => navigate('/')} className="bg-[#1e3a5f] hover:bg-[#2d5a8f] text-white">
              Back to Home
            </Button>
          </Card>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="container mx-auto px-4 py-8 max-w-3xl">
          <h1 className="text-3xl font-bold text-[#1e3a5f] text-center mb-2">Book a Pilot / Steerer</h1>
          <p className="text-gray-600 text-center mb-4">Complete the form below and our team will get back to you with availability and pricing.</p>

          {/* Live availability badge */}
          {availableCount !== null && availableCount > 0 && (
            <div className="mb-6 text-center" data-testid="pilots-available-badge">
              <button
                type="button"
                onClick={() => navigate('/pilots-available')}
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-300 text-green-800 rounded-full text-sm font-semibold hover:bg-green-100 transition-colors"
              >
                <Compass className="w-4 h-4" />
                {availableCount} pilot{availableCount === 1 ? '' : 's'} available now — browse the map
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Contact Details */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-[#1e3a5f] mb-4 border-b pb-2">Your Details</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Name *</Label>
                  <Input name="name" value={formData.name} onChange={handleChange} required placeholder="Full name" data-testid="pilot-name" />
                </div>
                <div>
                  <Label>Company Name</Label>
                  <Input name="company_name" value={formData.company_name} onChange={handleChange} placeholder="Company (optional)" />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input name="email" type="email" value={formData.email} onChange={handleChange} required placeholder="your@email.com" data-testid="pilot-email" />
                </div>
                <div>
                  <Label>Phone *</Label>
                  <Input name="phone" value={formData.phone} onChange={handleChange} required placeholder="04XX XXX XXX" data-testid="pilot-phone" />
                </div>
              </div>
            </Card>

            {/* Service Requirements */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-[#1e3a5f] mb-4 border-b pb-2">Service Requirements</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Service Required *</Label>
                  <select name="service_required" value={formData.service_required} onChange={handleChange} required className="w-full h-10 border rounded-md px-3 text-sm" data-testid="service-required-select">
                    <option value="">Select service</option>
                    <option value="pilot">Pilot</option>
                    <option value="steerer">Steerer</option>
                    <option value="both">Both</option>
                  </select>
                </div>
                <div>
                  <Label>Accredited Pilot/s Required *</Label>
                  <select name="accredited_pilots" value={formData.accredited_pilots} onChange={handleChange} required className="w-full h-10 border rounded-md px-3 text-sm" data-testid="accredited-select">
                    <option value="">Select</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                    <option value="not_sure">Not Sure</option>
                  </select>
                  {needsCallPrompt('accredited_pilots') && (
                    <p className="text-xs text-amber-700 mt-1 flex items-center gap-1"><Phone className="w-3 h-3" /> Please call us to discuss your requirements.</p>
                  )}
                </div>
                <div>
                  <Label>Number of Pilots Required *</Label>
                  <select name="number_of_pilots" value={formData.number_of_pilots} onChange={handleChange} required className="w-full h-10 border rounded-md px-3 text-sm" data-testid="num-pilots-select">
                    <option value="">Select</option>
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                    <option value="not_sure">Not Sure</option>
                  </select>
                  {needsCallPrompt('number_of_pilots') && (
                    <p className="text-xs text-amber-700 mt-1 flex items-center gap-1"><Phone className="w-3 h-3" /> Please call us to discuss your requirements.</p>
                  )}
                </div>
                <div>
                  <Label>Are Police Required? *</Label>
                  <select name="police_required" value={formData.police_required} onChange={handleChange} required className="w-full h-10 border rounded-md px-3 text-sm" data-testid="police-select">
                    <option value="">Select</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                    <option value="not_sure">Not Sure</option>
                  </select>
                  {needsCallPrompt('police_required') && (
                    <p className="text-xs text-amber-700 mt-1 flex items-center gap-1"><Phone className="w-3 h-3" /> Please call us to discuss your requirements.</p>
                  )}
                </div>
              </div>
            </Card>

            {/* Trip Details */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-[#1e3a5f] mb-4 border-b pb-2">Trip Details</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Date *</Label>
                  <Input name="travel_date" type="date" value={formData.travel_date} onChange={handleChange} required data-testid="travel-date" />
                </div>
                <div>
                  <Label>Time *</Label>
                  <Input name="travel_time" type="time" value={formData.travel_time} onChange={handleChange} required data-testid="travel-time" />
                </div>
                <div>
                  <Label>Load ID (if from our site)</Label>
                  <Input name="load_id" value={formData.load_id} onChange={handleChange} placeholder="e.g. a363c28c" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div>
                  <Label>Pickup Address *</Label>
                  <Input name="pickup_address" value={formData.pickup_address} onChange={handleChange} required placeholder="Full pickup address" data-testid="pickup-address" />
                </div>
                <div>
                  <Label>Destination Address *</Label>
                  <Input name="destination_address" value={formData.destination_address} onChange={handleChange} required placeholder="Full destination address" data-testid="destination-address" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div>
                  <Label>Pickup Contact Name *</Label>
                  <Input name="pickup_contact_name" value={formData.pickup_contact_name} onChange={handleChange} required placeholder="Contact at pickup location" data-testid="pickup-contact-name" />
                </div>
                <div>
                  <Label>Pickup Contact Phone *</Label>
                  <Input name="pickup_contact_phone" value={formData.pickup_contact_phone} onChange={handleChange} required placeholder="04XX XXX XXX" data-testid="pickup-contact-phone" />
                </div>
                <div>
                  <Label>Delivery Contact Name *</Label>
                  <Input name="destination_contact_name" value={formData.destination_contact_name} onChange={handleChange} required placeholder="Contact at delivery location" data-testid="delivery-contact-name" />
                </div>
                <div>
                  <Label>Delivery Contact Phone *</Label>
                  <Input name="destination_contact_phone" value={formData.destination_contact_phone} onChange={handleChange} required placeholder="04XX XXX XXX" data-testid="delivery-contact-phone" />
                </div>
              </div>
            </Card>

            {/* Load Information */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-[#1e3a5f] mb-4 border-b pb-2">Load Information</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>Load Type *</Label>
                  <Input name="load_type" value={formData.load_type} onChange={handleChange} required placeholder="e.g. Oversize machinery, Building, Wind turbine blade" data-testid="load-type" />
                </div>
                <div>
                  <Label>Load Height *</Label>
                  <Input name="load_height" value={formData.load_height} onChange={handleChange} required placeholder="e.g. 4.6m" data-testid="load-height" />
                </div>
                <div>
                  <Label>Load Width *</Label>
                  <Input name="load_width" value={formData.load_width} onChange={handleChange} required placeholder="e.g. 3.5m" data-testid="load-width" />
                </div>
                <div>
                  <Label>Load Length *</Label>
                  <Input name="load_length" value={formData.load_length} onChange={handleChange} required placeholder="e.g. 25m" data-testid="load-length" />
                </div>
              </div>
            </Card>

            {/* Permits */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-[#1e3a5f] mb-4 border-b pb-2">Permits & Surveys</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Permit Number</Label>
                  <Input name="permit_number" value={formData.permit_number} onChange={handleChange} placeholder="If applicable" />
                </div>
                <div>
                  <Label>High Load Permit Number</Label>
                  <Input name="high_load_permit_number" value={formData.high_load_permit_number} onChange={handleChange} placeholder="If applicable" />
                </div>
                <div>
                  <Label>ARTC or Other Rail Permit</Label>
                  <Input name="artc_rail_permit" value={formData.artc_rail_permit} onChange={handleChange} placeholder="If applicable" />
                </div>
                <div>
                  <Label>Telstra/Optus Permit</Label>
                  <Input name="telstra_optus_permit" value={formData.telstra_optus_permit} onChange={handleChange} placeholder="If applicable" />
                </div>
                <div>
                  <Label>Have You Had a Route Survey? *</Label>
                  <select name="route_survey" value={formData.route_survey} onChange={handleChange} required className="w-full h-10 border rounded-md px-3 text-sm" data-testid="route-survey-select">
                    <option value="">Select</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                    <option value="not_required">Not Required</option>
                  </select>
                </div>
              </div>

              {/* Permit Upload */}
              <div className="mt-4">
                <Label className="mb-2 block">Upload Permit Documents</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {permitDocs.map((doc, i) => (
                    <div key={i} className="flex items-center gap-1 bg-blue-50 border border-blue-200 rounded px-2 py-1 text-xs">
                      <span className="text-blue-700 max-w-[150px] truncate">{doc.name}</span>
                      <button type="button" onClick={() => removeDoc(i)} className="text-red-500 hover:text-red-700">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
                <label className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 cursor-pointer border border-blue-300 rounded px-3 py-1.5">
                  <Upload className="w-4 h-4" /> Upload Files
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" multiple className="hidden" onChange={handleFileUpload} />
                </label>
                <p className="text-xs text-gray-400 mt-1">PDF, JPG, PNG, DOC (max 10MB each)</p>
              </div>
            </Card>

            {/* Message */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-[#1e3a5f] mb-4 border-b pb-2">Additional Information</h2>
              <div>
                <Label>Message for Admin</Label>
                <Textarea name="message" value={formData.message} onChange={handleChange} placeholder="Any additional information or special requirements..." rows={4} />
              </div>
            </Card>

            {/* Submit */}
            <Button type="submit" disabled={loading} className="w-full bg-red-600 hover:bg-red-700 text-white text-lg py-6" data-testid="submit-pilot-booking">
              {loading ? 'Submitting...' : 'Submit Booking Request'}
            </Button>
          </form>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default BookPilot;
